import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Analytics } from '@vercel/analytics/react';
import { Layout } from './Layout';
import { Reports } from './pages/PreGamePrepPage';
import { Home } from './pages/HomePage';
import { Stats } from './pages/StatsPage';
import { SettingsPage } from './pages/SettingsPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { GlobalStyles } from './styles/GlobalStyles';
import { ReportProvider } from './contexts/ReportContext';
import { ChatProvider } from './contexts/ChatContext';
import { TeamProvider, useTeam } from './contexts/TeamContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { authService } from './services/authService';
import { setupGlobalResizeObserverFix } from './utils/resizeObserverFix';
import { startGlobalButtonTracking, trackEvent, setAnalyticsUser } from './utils/analytics';
import { TeamSelector } from './components/TeamSelector';
import { Modal } from './components/Modal';

const LoaderWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f0f0f;
  color: #fff;
  font-size: 1.1rem;
`;

type AppPage = 'home' | 'pregame' | 'stats' | 'livegame' | 'settings';
type ReportsMode = 'opponent' | 'user';
type AuthMode = 'login' | 'register' | 'verify-email';

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

const AppShell: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<AppPage>('home');
  const [previousPage, setPreviousPage] = useState<AppPage>('home');
  const [reportsMode, setReportsMode] = useState<ReportsMode>('opponent');
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const reportsRef = useRef<{ handleExportReport: () => void }>(null);
  const { user, loading } = useAuth();
  const previousUserRef = useRef<typeof user>(null);
  const [showTeamSelector, setShowTeamSelector] = useState(false);

  useEffect(() => {
    setupGlobalResizeObserverFix();

    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    const hasToken = urlParams.get('token');
    const isVerifyEmailPath = pathname.includes('verify-email');

    if (isVerifyEmailPath || hasToken) {
      setAuthMode('verify-email');
      if (hasToken && pathname !== '/verify-email') {
        window.history.replaceState({}, '', `/verify-email?token=${hasToken}`);
      }
    }
  }, []);

  useEffect(() => {
    const stopTracking = startGlobalButtonTracking();
    return stopTracking;
  }, []);

  useEffect(() => {
    setAnalyticsUser(user?.email);
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;
    const hasToken = urlParams.get('token');
    const isVerifyEmailPath = pathname.includes('verify-email');

    if ((isVerifyEmailPath || hasToken) && user) {
      const clearSession = async () => {
        try {
          await authService.logout();
        } catch {
          // Ignore logout errors in verification mode
        }
      };
      clearSession();
    }
  }, [user]);

  useEffect(() => {
    const wasLoggedOut = previousUserRef.current === null;
    const isNowLoggedIn = user !== null && !loading;
    const justLoggedIn = wasLoggedOut && isNowLoggedIn;
    previousUserRef.current = user;

    if (justLoggedIn && authMode !== 'verify-email') {
      setCurrentPage('home');
      try {
        localStorage.setItem('vermilion-current-page', 'home');
      } catch {}
    }
  }, [user, loading, authMode]);

  const handlePageChange = (page: AppPage) => {
    if (page !== 'settings' && currentPage !== 'settings') {
      setPreviousPage(page);
    } else if (currentPage !== 'settings' && page === 'settings') {
      setPreviousPage(currentPage);
    }

    setCurrentPage(page);
    trackEvent('page_change', { page });

    try {
      localStorage.setItem('vermilion-current-page', page);
    } catch {}
  };

  const handleBackFromSettings = () => {
    handlePageChange(previousPage);
  };

  const handleOpenTeamSelector = () => {
    setShowTeamSelector(true);
  };

  const handleCloseTeamSelector = () => {
    setShowTeamSelector(false);
  };

  const goToPreGame = () => {
    setReportsMode('opponent');
    handlePageChange('pregame');
  };

  const goToStats = () => {
    setReportsMode('user');
    handlePageChange('stats');
  };

  const goToLiveGame = () => {
    handlePageChange('livegame');
  };

  const handleExportReport = () => {
    if (reportsRef.current) {
      reportsRef.current.handleExportReport();
    }
  };

  const urlParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const hasToken = urlParams.get('token');
  const isVerifyEmailPath = pathname.includes('verify-email');
  const isVerifyEmailMode = isVerifyEmailPath || Boolean(hasToken) || authMode === 'verify-email';

  if (loading) {
    return (
      <ThemeProvider>
        <GlobalStyles />
        <LoaderWrapper>Checking your session…</LoaderWrapper>
      </ThemeProvider>
    );
  }

  if (isVerifyEmailMode && authMode === 'verify-email') {
    return (
      <ThemeProvider>
        <GlobalStyles />
        <VerifyEmailPage
          onGoToLogin={() => {
            if (typeof window !== 'undefined') {
              window.history.replaceState({}, '', '/login');
            }
            setAuthMode('login');
          }}
        />
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider>
        <GlobalStyles />
        {authMode === 'login' ? (
          <LoginPage onSwitchToRegister={() => setAuthMode('register')} />
        ) : (
          <RegisterPage onSwitchToLogin={() => setAuthMode('login')} />
        )}
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <GlobalStyles />
      <TeamProvider>
        <AppContent 
          currentPage={currentPage}
          previousPage={previousPage}
          reportsMode={reportsMode}
          reportsRef={reportsRef}
          onPageChange={handlePageChange}
          onBackFromSettings={handleBackFromSettings}
          onOpenTeamSelector={handleOpenTeamSelector}
        />
      </TeamProvider>
    </ThemeProvider>
  );
};

interface AppContentProps {
  currentPage: AppPage;
  previousPage: AppPage;
  reportsMode: ReportsMode;
  reportsRef: React.RefObject<{ handleExportReport: () => void } | null>;
  onPageChange: (page: AppPage) => void;
  onBackFromSettings: () => void;
  onOpenTeamSelector: () => void;
}

const AppContent: React.FC<AppContentProps> = ({
  currentPage,
  previousPage,
  reportsMode,
  reportsRef,
  onPageChange,
  onBackFromSettings,
  onOpenTeamSelector
}) => {
  const { selectedLeague, matchupConfig } = useTeam();
  const [showTeamSelector, setShowTeamSelector] = useState(false);

  const handleOpenTeamSelector = () => {
    setShowTeamSelector(true);
  };

  const handleCloseTeamSelector = () => {
    setShowTeamSelector(false);
  };

  // Validate complete configuration before allowing navigation
  const validateCompleteConfiguration = (): boolean => {
    // Check league configuration
    if (!selectedLeague || !selectedLeague.abbreviation || selectedLeague.abbreviation.trim() === '') {
      console.log('❌ No league configured - opening TeamSelector');
      handleOpenTeamSelector();
      return false;
    }

    // Check matchup configuration
    if (!matchupConfig) {
      console.log('❌ No matchup configuration - opening TeamSelector');
      handleOpenTeamSelector();
      return false;
    }

    // Check user team
    if (!matchupConfig.user_team || !matchupConfig.user_team.teamId || matchupConfig.user_team.teamId.trim() === '') {
      console.log('❌ No user team configured - opening TeamSelector');
      handleOpenTeamSelector();
      return false;
    }

    // Check opponent team
    if (!matchupConfig.opponent_team || !matchupConfig.opponent_team.teamId || matchupConfig.opponent_team.teamId.trim() === '') {
      console.log('❌ No opponent team configured - opening TeamSelector');
      handleOpenTeamSelector();
      return false;
    }

    // Check season
    if (!matchupConfig.season || matchupConfig.season.trim() === '') {
      console.log('❌ No season configured - opening TeamSelector');
      handleOpenTeamSelector();
      return false;
    }

    console.log('✅ Complete configuration validated successfully');
    return true;
  };

  const goToPreGame = () => {
    if (!validateCompleteConfiguration()) return;
    onPageChange('pregame');
  };

  const goToStats = () => {
    if (!validateCompleteConfiguration()) return;
    onPageChange('stats');
  };

  const goToLiveGame = () => {
    if (!validateCompleteConfiguration()) return;
    onPageChange('livegame');
  };

  const handleExportReport = () => {
    if (reportsRef.current) {
      reportsRef.current.handleExportReport();
    }
  };

  return (
    <>
      <ChatProvider>
        <ReportProvider>
          <Layout
            currentPage={currentPage}
            onPageChange={onPageChange}
            onExportReport={handleExportReport}
            onGoToOpponentReports={goToPreGame}
            onGoToUserReports={goToStats}
          >
            {currentPage === 'home' && (
              <Home onGoToOpponentReports={goToPreGame} onGoToUserReports={goToStats} onGoToLiveGame={goToLiveGame} />
            )}
            {currentPage === 'pregame' && (
              <Reports
                ref={reportsRef}
                onGoToHome={() => onPageChange('home')}
                userTeamMode={reportsMode === 'user'}
              />
            )}
            {currentPage === 'stats' && <Stats />}
            {currentPage === 'settings' && <SettingsPage onBack={onBackFromSettings} />}
            {currentPage === 'livegame' && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '100vh',
                  color: 'white',
                  textAlign: 'center',
                  padding: '2rem'
                }}
              >
                <h1>Live Game Assistant</h1>
                <p>This feature is coming soon!</p>
                <button
                  onClick={() => onPageChange('home')}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#CC3333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Back to Home
                </button>
              </div>
            )}
          </Layout>
          <Analytics />
        </ReportProvider>
      </ChatProvider>
      <Modal isOpen={showTeamSelector} onClose={handleCloseTeamSelector}>
        <TeamSelector onClose={handleCloseTeamSelector} />
      </Modal>
    </>
  );
};

export default App;